import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Field } from "../../components/common/Field";
import { StatusBanner } from "../../components/common/StatusBanner";
import { KioskLayout } from "../../components/kiosk/KioskLayout";
import {
  SignaturePad,
  type SignaturePadHandle,
} from "../../components/kiosk/SignaturePad";
import { useIdleTimeout } from "../../hooks/useIdleTimeout";
import { useSessionStorageState } from "../../hooks/useSessionStorageState";
import { kioskCheckin } from "../../services/rpc";
import { deleteSignature, uploadSignature } from "../../services/storage";
import type { CheckinDraft, CheckinFieldErrors } from "../../types/domain";
import { toErrorMessage } from "../../utils/errors";
import { focusNextFieldOnEnter, scrollToFirstError } from "../../utils/kioskForm";
import { sanitizePersonText, validateCheckinDraft } from "../../utils/validation";

const PRIVACY_POLICY_TEXT = `PRIVACY POLICY
(Informazioni sulla protezione dei dati personali all’Interessato)
Gentile Utente, con la presente Informativa sulla privacy informiamo sulle modalità di trattamento e protezione dei
dati personali nel rispetto della normativa in materia di protezione dei dati personali.
1. IDENTITÀ E DATI DI CONTATTO DEL TITOLARE DEL TRATTAMENTO: 3i engineering S.r.l., con sede in 15121
Alessandria (AL), Via Galimberti, 36, email del Titolare: info@gruppo3i.it, telefono +39 0131 223600 e email dedicata
alla privacy: privacy@gruppo3i.it
2. DATI DI CONTATTO DEL RESPONSABILE DELLA PROTEZIONE DEI DATI (DPO): il Titolare del trattamento ha
provveduto alla designazione del Responsabile per la protezione dei dati (DPO), raggiungibile all’indirizzo e-mail:
dpo@gruppo3i.it
3. TIPOLOGIA DEI DATI PERSONALI
- DATI DI NAVIGAZIONE I sistemi informatici preposti al funzionamento di questo Sito acquisiscono alcuni dati di
carattere personale trasmessi mediante protocolli sicuri. Tale raccolta non è in alcun modo finalizzata all’identificazione
dell’utente ma ciò potrebbe avvenire attraverso elaborazioni ed associazioni con dati detenuti da terzi. In questa
categoria di dati rientrano gli indirizzi IP, l’identificativo del browser, gli indirizzi in notazione URI (Uniform Resource
Identifier) delle risorse richieste, l’orario della richiesta, il metodo utilizzato nel sottoporre la richiesta al server, il codice
numerico indicante lo stato della risposta data dal server (ad es. buon fine, errore) ed altri parametri relativi al sistema
operativo e all’ambiente informatico dell’utente. Questi dati vengono utilizzati al solo fine di ricavare informazioni di
tipo statistico (quindi sono aggregati anonimi) e per controllare il corretto funzionamento del sito.
- DATI PERSONALI FORNITI VOLONTARIAMENTE DALL’UTENTE A parte quanto previsto per i dati di navigazione, il
conferimento dei dati personali da parte degli utenti è facoltativo. Si tratta dei dati forniti volontariamente dal visitatore
del sito, ad esempio compilando i form, che saranno trattati al solo scopo di eseguire il servizio richiesto.
4. FINALITÀ E BASI GIURIDICHE DEL TRATTAMENTO DEI DATI PERSONALI
Di seguito si illustrano le specifiche finalità:
a) SERVIZI SUL SITO
FINALITA’: per la finalità della corretta e completa esecuzione dell’incarico affidato:
- la fruizione dei servizi, ovvero, la navigazione sul sito
- la risposta ai messaggi
- la partecipazione a corsi dal vivo e online
- la fruizione di contenuti formativi e/o promozionali
BASE GIURIDICA PER IL TRATTAMENTO DEI DATI PERSONALI: è basata sull’esecuzione di misure precontrattuali
od obblighi contrattuali, la comunicazione dei dati è necessaria per l’instaurazione del rapporto e per poter
adempiere al mandato.
b) ATTIVITÀ DI INFORMAZIONE SULLE NOVITÀ DI TUTELA E NORMATIVE:
FINALITÀ: le attività di Informazione sulle novità di tutela e normative, ai fini di vendita diretta di servizi e/o
prodotti analoghi a quelli già acquistati dal Cliente/Utente, ovvero, per ricevere comunicazioni promozionali
sugli eventi e le iniziative promozionali già acquistati.
BASE GIURIDICA PER IL TRATTAMENTO: è basato sul perseguimento del legittimo interesse del titolare del
trattamento, bilanciato rispetto agli interessi o ai diritti e alle libertà fondamentali dell’interessato, ai sensi
dell’art. 6 par. 1 lettera f) del GDPR anche in virtù di quanto stabilito dall’art. 130 comma 4 del D.lgs. 196/2003
in merito al c.d. “Soft-Spam”.
L’utente, al momento della raccolta e in occasione dell’invio di ogni comunicazione effettuata per le finalità di
cui al presente comma, è informato della possibilità di opporsi in ogni momento al trattamento, in maniera
agevole e gratuitamente, anche inviando una comunicazione all’indirizzo: privacy@gruppo3i.it e/o al DPO
all’email: dpo@gruppo3i.it
Le attività di promozione e marketing avverranno attraverso i seguenti canali di comunicazione:
- email (newsletter) posta elettronica
- posta cartacea all’indirizzo comunicato
L’utente potrà scegliere quali canali di comunicazione preferisce e revocare, opporsi, ovvero limitare il
trattamento per uno o alcuni di questi.
BASE GIURIDICA PER IL TRATTAMENTO DEI DATI PERSONALI: il trattamento è basato sul consenso prestato per
questa specifica finalità e fino alla revoca, la comunicazione dei dati non è obbligatoria ma senza il conferimento
dei dati non sarà possibile erogare il servizio di marketing.
La cancellazione dall’attività di INFORMAZIONE SULLE NOVITÀ DI TUTELA E NORMATIVE (attraverso
l’opposizione) non comporta la cancellazione dell’utenza e dal gestionale. La comunicazione dei dati è
necessaria per la sola fruizione dei servizi di marketing.
c) ATTIVITA’ DI MARKETING
FINALITÀ: per ricevere, anche attraverso la newsletter, ovvero in maniera diretta e su misura per il Cliente,
comunicazioni di marketing, promozionali, per la vendita diretta di prodotti e servizi, inviti per la partecipazione
ai corsi, agli eventi, alle fiere e per offerte, omaggi e incentive.
Le attività di promozione e marketing avverranno attraverso i seguenti canali di comunicazione:
- e-mail posta elettronica
- posta cartacea all’indirizzo comunicato
BASE GIURIDICA PER IL TRATTAMENTO DEI DATI PERSONALI: il trattamento è basato sul consenso prestato per
questa specifica finalità e fino alla revoca, la comunicazione dei dati non è obbligatoria ma senza il conferimento
dei dati non sarà possibile erogare il servizio di marketing.
La cancellazione dall’attività di MARKETING (attraverso la revoca, la limitazione e/o l’opposizione) non
comporta la cancellazione dell’utenza e dal gestionale. La comunicazione dei dati è necessaria per la sola
fruizione dei servizi di marketing.
d) GESTIONE DELLE ATTIVITÀ ULTERIORI
FINALITÀ: per la finalità della corretta e completa esecuzione e gestione degli adempimenti imposti dalle
normative vigenti (es. la fatturazione).
BASE GIURIDICA PER IL TRATTAMENTO DEI DATI PERSONALI: è basata sulla necessità di adempiere un obbligo
legale.
FINALITÀ: per la finalità della corretta e completa esecuzione della tutela legale del Titolare del Trattamento e
delle su legittime finalità (es. recupero crediti)
BASE GIURIDICA PER IL TRATTAMENTO DEI DATI PERSONALI: è basata sulla necessità di accertare, esercitare o
difendere un diritto in sede giudiziaria
e) CANDIDATURE
FINALITÀ: per la finalità della corretta e completa esecuzione dell’incarico affidato: l’invio delle candidature e
il caricamento dei documenti (es. il CV), la risposta alle informazioni richieste dal candidato, la creazione di
un’area utente e la corretta gestione degli adempimenti imposti dalle normative vigenti.
BASE GIURIDICA PER IL TRATTAMENTO DEI DATI PERSONALI: è basata sull’esecuzione di misure precontrattuali
od obblighi contrattuali, la comunicazione dei dati è necessaria per l’instaurazione del rapporto e per poter
adempiere al mandato. Per le eventuali categorie particolari di dati personali, il trattamento è necessario per
assolvere gli obblighi ed esercitare i diritti specifici del titolare del trattamento o dell'interessato in materia di
diritto del lavoro e della sicurezza sociale e protezione sociale, in presenza di garanzie appropriate per i diritti
fondamentali e gli interessi dell'interessato; i dati saranno conservati per un periodo massimo di 12 mesi.
In ogni momento, Lei potrà rileggere l’informativa e modificare i consensi precedentemente forniti, verificare e/o
modificare lo stato dei servizi attivi ed eventualmente richiedere servizi aggiuntivi.
5. DESTINATARI DEI DATI PERSONALI
Le categorie di destinatari dei dati personali possono essere: società di consulenza digital e digital marketing, agenzie di
comunicazione, società di fornitura di servizi informatici, provider, professionisti ed esperti di marketing. La
comunicazione dei dati non è obbligatoria ma senza il conferimento dei dati non sarà possibile erogare il servizio di
marketing.
6. PERIODO DI CONSERVAZIONE DEI DATI E CRITERI UTILIZZATI PER LA SUA DETERMINAZIONE
I dati personali degli utenti saranno conservati in conformità alla normativa vigente in materia, per un periodo di tempo
non superiore a quello necessario agli scopi per i quali sono stati raccolti e trattati, tenendo in considerazione: la
necessità di continuare a conservare i dati personali raccolti per offrire i servizi concordati con l’Interessato o per
tutelare l’interesse legittimo del Titolare, così come descritto nelle finalità sopraindicate e l’esistenza di specifici obblighi
normativi o contrattuali che rendono necessario il trattamento e la conservazione dei dati per determinati periodi di
tempo. Nel rispetto dei principi di proporzionalità e necessità, i dati personali saranno conservati in una forma che
consenta l’identificazione degli interessati per un arco di tempo non superiore al conseguimento delle finalità per le
quali gli stessi sono trattati, ossia tenendo in considerazione: la necessità di continuare a conservare i dati personali
raccolti per offrire i servizi concordati con l’utente o per tutelare l’interesse legittimo del Titolare, così come descritto
nelle finalità sopraindicate e l’esistenza di specifici obblighi normativi (normativa codicistica, normativa in materia di
antiriciclaggio, normativa in materia di servizi di investimento, normativa sul monitoraggio fiscale, ecc.) o contrattuali
che rendono necessario il trattamento e la conservazione dei dati per determinati periodi di tempo.
7. DIRITTI DELL’INTERESSATO E DIRITTO DI PROPORRE RECLAMO ALL’AUTORITÀ DI CONTROLLO
L’Interessato potrà esercitare il diritto, in qualsiasi momento, inviando una comunicazione al Titolare del trattamento
all’indirizzo e-mail: privacy@gruppo3i.it e/o al DPO all’email: dpo@gruppo3i.it
• alla revoca del consenso;
• alla revoca del consenso per la finalità di marketing;
• all’accesso ai dati personali;
• alla rettifica dei dati personali;
• alla cancellazione (c.d. diritto all’oblio) dei dati personali;
• alla limitazione del trattamento dei dati personali;
• alla limitazione del trattamento dei dati personali dei canali di comunicazione per il marketing e il
 marketing diretto;
• all’opposizione al trattamento dei dati personali;
• all’opposizione al trattamento dei dati per la finalità̀di marketing diretto;
• all’opposizione al trattamento con modalità automatizzate, compresa la profilazione;
• alla portabilità dei dati personali;
• alla proposizione del reclamo al Garante per la Protezione dei Dati Personali GPDP e/o ad altra Autorità
Garante, qualora ritenga che i diritti siano stati violati dal Titolare e/o da un terzo.
8. MODALITÀ E LUOGO DEL TRATTAMENTO DEI DATI PERSONALI
I dati personali sono trattati con strumenti cartacei ed elettronici, registrati e custoditi su database digitali, trattati
presso la sede aziendale e presso i soggetti autorizzati quali Responsabili del trattamento dei dati in Italia e in Europa.`;

const INITIAL_DRAFT: CheckinDraft = {
  firstName: "",
  lastName: "",
  company: "",
  signatureDataUrl: "",
  privacyAccepted: false,
  emergencyPlanAccepted: false,
};

export function CheckinPage() {
  const navigate = useNavigate();
  const signatureRef = useRef<SignaturePadHandle | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const { value: draft, setValue: setDraft, clear } = useSessionStorageState(
    "kiosk-checkin-draft",
    INITIAL_DRAFT,
  );
  const resolvedDraft: CheckinDraft = {
    ...INITIAL_DRAFT,
    ...draft,
  };
  const [fieldErrors, setFieldErrors] = useState<CheckinFieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useIdleTimeout(120000, () => {
    clear();
    navigate("/", { replace: true });
  });

  function updateDraft(nextValues: Partial<CheckinDraft>) {
    setDraft({
      ...resolvedDraft,
      ...nextValues,
    });
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validateCheckinDraft(resolvedDraft);
    setFieldErrors(nextErrors);
    setErrorMessage(null);

    if (Object.keys(nextErrors).length > 0) {
      scrollToFirstError(formRef.current);
      return;
    }

    setIsSubmitting(true);

    let signaturePath = "";

    try {
      const signatureBlob = await signatureRef.current?.toBlob();
      if (!signatureBlob) {
        throw new Error("La firma non è disponibile.");
      }

      signaturePath = await uploadSignature("checkin", signatureBlob);
      await kioskCheckin(
        {
          ...resolvedDraft,
          firstName: sanitizePersonText(resolvedDraft.firstName),
          lastName: sanitizePersonText(resolvedDraft.lastName),
          company: sanitizePersonText(resolvedDraft.company),
        },
        signaturePath,
      );
      clear();
      navigate("/success/checkin", { replace: true });
    } catch (error) {
      if (signaturePath) {
        deleteSignature(signaturePath).catch(() => undefined);
      }

      setErrorMessage(
        toErrorMessage(error, "Il check-in non è andato a buon fine. Riprova."),
      );
      scrollToFirstError(formRef.current);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <KioskLayout
      title="Nuovo ingresso"
      subtitle="Compila i dati essenziali e raccogli la firma del visitatore."
    >
      <form
        ref={formRef}
        className="kiosk-form"
        onSubmit={handleSubmit}
        onKeyDown={focusNextFieldOnEnter}
        noValidate
      >
        <div className="form-grid">
          <Field
            label="Nome"
            error={fieldErrors.firstName}
            inputProps={{
              value: resolvedDraft.firstName,
              onChange: (event) => updateDraft({ firstName: event.target.value }),
              autoComplete: "given-name",
              autoCapitalize: "words",
              autoCorrect: "off",
              spellCheck: false,
              enterKeyHint: "next",
              placeholder: "Mario",
            }}
          />
          <Field
            label="Cognome"
            error={fieldErrors.lastName}
            inputProps={{
              value: resolvedDraft.lastName,
              onChange: (event) => updateDraft({ lastName: event.target.value }),
              autoComplete: "family-name",
              autoCapitalize: "words",
              autoCorrect: "off",
              spellCheck: false,
              enterKeyHint: "next",
              placeholder: "Rossi",
            }}
          />
          <Field
            label="Azienda"
            error={fieldErrors.company}
            inputProps={{
              value: resolvedDraft.company,
              onChange: (event) => updateDraft({ company: event.target.value }),
              autoComplete: "organization",
              autoCapitalize: "words",
              autoCorrect: "off",
              spellCheck: false,
              enterKeyHint: "done",
              placeholder: "Azienda S.p.A.",
            }}
          />
        </div>
        <SignaturePad
          ref={signatureRef}
          label="Firma visitatore"
          valueDataUrl={resolvedDraft.signatureDataUrl}
          error={fieldErrors.signature}
          onChange={(signatureDataUrl) => updateDraft({ signatureDataUrl })}
        />
        <section className="selection-panel">
          <div className="selection-panel__header">
            <div>
              <h2>Consensi obbligatori</h2>
            </div>
          </div>
          <details className="policy-details">
            <summary>Visualizza la Privacy Policy</summary>
            <div className="policy-text">{PRIVACY_POLICY_TEXT}</div>
          </details>
          <div className="consent-option">
            <label className="consent-checkbox">
              <input
                type="checkbox"
                checked={resolvedDraft.privacyAccepted}
                onChange={(event) =>
                  updateDraft({ privacyAccepted: event.target.checked })
                }
              />
              <span>
                Dichiaro di aver preso visione e di accettare la Privacy Policy
              </span>
            </label>
            {fieldErrors.privacyAccepted ? (
              <span className="field__error">{fieldErrors.privacyAccepted}</span>
            ) : null}
          </div>
          <div className="consent-option">
            <label className="consent-checkbox">
              <input
                type="checkbox"
                checked={resolvedDraft.emergencyPlanAccepted}
                onChange={(event) =>
                  updateDraft({ emergencyPlanAccepted: event.target.checked })
                }
              />
              <span>
                {"Confermo che mi \u00e8 stato illustrato il piano di emergenza"}
              </span>
            </label>
            {fieldErrors.emergencyPlanAccepted ? (
              <span className="field__error">
                {fieldErrors.emergencyPlanAccepted}
              </span>
            ) : null}
          </div>
        </section>
        {errorMessage ? <StatusBanner tone="error" message={errorMessage} /> : null}
        <div className="toolbar-actions toolbar-actions--end">
          <button
            className="ghost-button"
            type="button"
            onClick={() => {
              clear();
              navigate("/");
            }}
          >
            Annulla
          </button>
          <button className="primary-button" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Registrazione..." : "Conferma ingresso"}
          </button>
        </div>
      </form>
    </KioskLayout>
  );
}
